import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, TrendingUp, Target, Sparkles, Check, ChevronRight } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ScoreGauge from '@/components/ScoreGauge';

const MEMBERSHIP_URL = 'https://www.wearetheweave.co.uk/the-1-club';
const TIDYCAL_URL = 'https://tidycal.com/the-weave/funding-clinic';

const pillars = [
    {
        icon: Building2,
        label: 'Business structure',
        text: 'Whether a CIC, charity, limited company or partnership — your structure shapes the funding routes open to you.',
        accent: 'bg-weave-yellow text-weave-ink',
    },
    {
        icon: TrendingUp,
        label: 'Scalability',
        text: 'How far can your model grow beyond you. Key for equity routes, useful signal for every funder.',
        accent: 'bg-weave-red text-white',
    },
    {
        icon: Sparkles,
        label: 'Traction',
        text: 'Evidence the idea is working — pilots, paying customers, repeat revenue, retention and growth.',
        accent: 'bg-weave-teal text-white',
    },
    {
        icon: Target,
        label: 'Funding goals',
        text: 'Amount, purpose, timeline and flexibility. We check whether your ask is realistic and route-appropriate.',
        accent: 'bg-weave-ink text-weave-cream',
    },
];

export default function Landing() {
    return (
        <div className="min-h-screen bg-weave-cream">
            <Header />

            {/* HERO — cream */}
            <section className="grain-bg">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid md:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="md:col-span-7"
                    >
                        <div className="text-overline text-weave-red mb-6" data-testid="hero-overline">
                            Credit-score-style check · free · 4 minutes
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-weave-ink leading-[0.95]">
                            Get your <span className="marker-underline">Funding Readiness</span> Score.
                        </h1>
                        <p className="mt-6 text-lg text-weave-ink-soft max-w-xl leading-relaxed">
                            A credit-score-style check that tells you how funding-ready your
                            business is — and which route is most likely to fit. Grant,
                            accelerator, loan, equity or crowdfunding. Clear answers, no
                            pitch jargon.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link to="/check" data-testid="hero-cta-primary">
                                <button className="pill-btn bg-weave-red text-white hover:bg-weave-red-deep text-base shadow-sm">
                                    Check my Funding Readiness Score
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </Link>
                            <a
                                href={MEMBERSHIP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-cta-member"
                            >
                                <button className="pill-btn bg-transparent border-2 border-weave-ink text-weave-ink hover:bg-weave-ink hover:text-white text-base">
                                    Become a member
                                </button>
                            </a>
                        </div>
                        <p className="mt-6 text-sm text-weave-muted max-w-md">
                            Free Funding Readiness Score. Members unlock tailored monthly funding
                            updates and two 1-hour funding deep dives each year.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="md:col-span-5 flex flex-col items-center"
                    >
                        <div className="bg-white organic-border border-2 border-weave-border p-6 md:p-10 shadow-sm relative">
                            <div className="absolute -top-3 -right-3 bg-weave-yellow text-weave-ink text-overline px-3 py-1 rounded-full">
                                Sample
                            </div>
                            <ScoreGauge score={68} size={300} label="Sample score" />
                            <div className="mt-4 grid grid-cols-2 gap-3 text-xs font-mono">
                                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-band-early" /> Early</div>
                                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-band-emerging" /> Emerging</div>
                                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-band-progressing" /> Progressing</div>
                                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-band-investment" /> Investment-ready</div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* 4 PILLARS — teal block */}
            <section id="how-it-works" className="bg-weave-teal text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
                    <div className="grid md:grid-cols-12 gap-10 mb-12">
                        <div className="md:col-span-5">
                            <div className="text-overline text-weave-yellow mb-4">How the score works</div>
                            <h2 className="font-display text-4xl md:text-5xl leading-[1.05]">
                                Four signals. <br />
                                <span className="marker-underline text-weave-ink">One honest read.</span>
                            </h2>
                        </div>
                        <p className="md:col-span-6 md:col-start-7 text-lg leading-relaxed text-white/90">
                            We combine your answers across four dimensions plus your community
                            and lead-investor signals to produce a headline score, a readiness
                            band, and the funding route most likely to suit your business
                            right now — including grant, accelerator, equity, loan or
                            crowdfunding.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {pillars.map((p, i) => (
                            <motion.div
                                key={p.label}
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: i * 0.08 }}
                                className="bg-white text-weave-ink organic-border p-7 card-lift"
                                data-testid={`pillar-${p.label.toLowerCase().replace(/ /g, '-')}`}
                            >
                                <div className={`w-11 h-11 rounded-full flex items-center justify-center mb-5 ${p.accent}`}>
                                    <p.icon className="w-5 h-5" />
                                </div>
                                <div className="text-overline text-weave-muted mb-2">0{i + 1}</div>
                                <h3 className="font-display text-2xl text-weave-ink mb-2">{p.label}</h3>
                                <p className="text-sm text-weave-ink-soft leading-relaxed">{p.text}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* THREE LAYERS — cream */}
            <section className="bg-weave-cream">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
                    <div className="text-overline text-weave-red mb-4">A three-layer funnel</div>
                    <h2 className="font-display text-4xl md:text-5xl text-weave-ink max-w-2xl leading-[1.05] mb-16">
                        Free diagnosis. Tailored updates. Strategic deep dives.
                    </h2>

                    <div className="grid md:grid-cols-3 gap-5">
                        <div className="p-8 organic-border bg-white border-2 border-weave-border card-lift" data-testid="layer-free">
                            <div className="text-overline text-weave-muted">Layer 01 · Free</div>
                            <h3 className="font-display text-3xl text-weave-ink mt-3 mb-4">Funding Readiness Score</h3>
                            <p className="text-sm text-weave-ink-soft mb-6 leading-relaxed">
                                Take the checker and get the full diagnostic + interpretation:
                                a 0–100 score, readiness band, likely route and your top
                                strength, top gap and recommended next step. PDF download
                                included.
                            </p>
                            <ul className="text-sm text-weave-ink-soft space-y-2">
                                <li className="flex gap-2"><Check className="w-4 h-4 text-weave-teal mt-0.5 shrink-0" /> Headline 0–100 score &amp; band</li>
                                <li className="flex gap-2"><Check className="w-4 h-4 text-weave-teal mt-0.5 shrink-0" /> Likely funding route</li>
                                <li className="flex gap-2"><Check className="w-4 h-4 text-weave-teal mt-0.5 shrink-0" /> Full pillar interpretation</li>
                                <li className="flex gap-2"><Check className="w-4 h-4 text-weave-teal mt-0.5 shrink-0" /> Branded PDF report</li>
                            </ul>
                        </div>

                        <div className="p-8 organic-border bg-weave-yellow border-2 border-weave-yellow card-lift" data-testid="layer-member">
                            <div className="text-overline text-weave-ink">Layer 02 · Member</div>
                            <h3 className="font-display text-3xl text-weave-ink mt-3 mb-4">Practical funding scans</h3>
                            <p className="text-sm text-weave-ink mb-6 leading-relaxed">
                                Monthly hand-curated scans of currently-available UK funding —
                                our team searches the live database against your criteria
                                and returns matched opportunities to your inbox and dashboard.
                            </p>
                            <ul className="text-sm text-weave-ink space-y-2">
                                <li className="flex gap-2"><Check className="w-4 h-4 text-weave-ink mt-0.5 shrink-0" /> Monthly funding matches</li>
                                <li className="flex gap-2"><Check className="w-4 h-4 text-weave-ink mt-0.5 shrink-0" /> AI pitch-deck 4Cs review</li>
                                <li className="flex gap-2"><Check className="w-4 h-4 text-weave-ink mt-0.5 shrink-0" /> Live opportunity dashboard</li>
                            </ul>
                        </div>

                        <div className="p-8 organic-border bg-weave-ink text-weave-cream card-lift" data-testid="layer-deepdive">
                            <div className="text-overline text-weave-yellow">Layer 03 · Member</div>
                            <h3 className="font-display text-3xl mt-3 mb-4">Regular Funding Clinics</h3>
                            <p className="text-sm text-weave-cream/85 mb-6 leading-relaxed">
                                Book a 1-hour Funding Clinic with our funding leads whenever
                                you need — discuss your route, readiness gaps and next steps.
                                Two sessions a year included with membership.
                            </p>
                            <ul className="text-sm text-weave-cream/85 space-y-2">
                                <li className="flex gap-2"><Check className="w-4 h-4 text-weave-yellow mt-0.5 shrink-0" /> Two 1-hour sessions / year</li>
                                <li className="flex gap-2"><Check className="w-4 h-4 text-weave-yellow mt-0.5 shrink-0" /> Strategic readiness coaching</li>
                                <li className="flex gap-2"><Check className="w-4 h-4 text-weave-yellow mt-0.5 shrink-0" /> Book directly via TidyCal</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* SAMPLE RESULT — sand */}
            <section className="bg-weave-sand">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-6">
                        <div className="text-overline text-weave-red mb-4">What you'll see</div>
                        <h2 className="font-display text-4xl md:text-5xl text-weave-ink leading-[1.05] mb-6">
                            A result that actually helps.
                        </h2>
                        <p className="text-weave-ink-soft leading-relaxed mb-6">
                            Not just a number. You'll get clear signals for each pillar, your
                            top strength, your most important gap, and a recommended next step.
                        </p>
                        <div className="space-y-4">
                            <div className="flex gap-3 items-start">
                                <div className="w-2 h-2 rounded-full bg-weave-teal mt-2.5" />
                                <div>
                                    <div className="font-display font-bold text-xl text-weave-ink">Top strength</div>
                                    <div className="text-sm text-weave-ink-soft">Clear mission &amp; early validation</div>
                                </div>
                            </div>
                            <div className="flex gap-3 items-start">
                                <div className="w-2 h-2 rounded-full bg-weave-yellow mt-2.5" />
                                <div>
                                    <div className="font-display font-bold text-xl text-weave-ink">Top gap</div>
                                    <div className="text-sm text-weave-ink-soft">Stronger traction evidence would improve readiness</div>
                                </div>
                            </div>
                            <div className="flex gap-3 items-start">
                                <div className="w-2 h-2 rounded-full bg-weave-red mt-2.5" />
                                <div>
                                    <div className="font-display font-bold text-xl text-weave-ink">Likely route</div>
                                    <div className="text-sm text-weave-ink-soft">Grant-led with accelerator potential</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <div className="bg-white organic-border border-2 border-weave-border p-8 relative">
                            <div className="text-overline text-weave-muted mb-2">Your score</div>
                            <div className="flex items-end gap-3 mb-5">
                                <div className="font-display text-7xl text-weave-ink leading-none">68</div>
                                <div className="text-weave-muted mb-2">/ 100</div>
                            </div>
                            <div className="h-2 bg-weave-border rounded-full overflow-hidden mb-6">
                                <div className="h-full bg-band-progressing rounded-full" style={{ width: '68%' }} />
                            </div>
                            <div className="space-y-3 text-sm text-weave-ink-soft">
                                <Row label="Business structure" value="19 / 25" />
                                <Row label="Scalability" value="15 / 25" />
                                <Row label="Traction" value="17 / 25" />
                                <Row label="Funding goals" value="17 / 25" last />
                            </div>
                            <div className="mt-6 p-4 bg-weave-yellow rounded-xl flex items-center gap-3 text-sm text-weave-ink">
                                <Sparkles className="w-4 h-4 shrink-0" />
                                Free score &amp; full interpretation. Members get practical funding scans &amp; clinics.
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* MEMBERSHIP CTA — red */}
            <section id="membership" className="bg-weave-red text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7">
                        <div className="text-overline text-weave-yellow mb-4">Membership</div>
                        <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-6">
                            Take it further with The 1% Club.
                        </h2>
                        <p className="text-white/90 leading-relaxed mb-8 max-w-xl">
                            Your score, interpretation and PDF are free. Membership is the
                            engine room — regular Funding Clinics with our funding leads,
                            monthly practical scans of currently-available UK funding, and
                            AI pitch-deck review.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a href={MEMBERSHIP_URL} target="_blank" rel="noopener noreferrer" data-testid="membership-external-cta">
                                <button className="pill-btn bg-weave-yellow text-weave-ink hover:bg-white">
                                    Become a member <ChevronRight className="w-4 h-4" />
                                </button>
                            </a>
                            <a href={TIDYCAL_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-tidycal-link">
                                <button className="pill-btn bg-transparent border-2 border-white text-white hover:bg-white hover:text-weave-red">
                                    Book a funding clinic
                                </button>
                            </a>
                        </div>
                    </div>
                    <ul className="md:col-span-5 space-y-4 text-white/95">
                        {[
                            'Regular Funding Clinics — book 1-hour sessions when you need them',
                            'Monthly practical scans of available UK funding',
                            'AI pitch-deck 4Cs review (with consent)',
                            'Live opportunity dashboard',
                            'Priority support from the team',
                        ].map((benefit) => (
                            <li key={benefit} className="flex gap-3 items-start">
                                <Check className="w-5 h-5 text-weave-yellow mt-0.5 shrink-0" />
                                <span>{benefit}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function Row({ label, value, last }) {
    return (
        <div className={`flex justify-between ${!last ? 'border-b border-weave-border pb-2' : ''}`}>
            <span>{label}</span>
            <span className="font-mono">{value}</span>
        </div>
    );
}
